import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter,Subscription } from 'rxjs';

import { ConfirmationModalComponent } from '../../components/modals/confirmation-modal/confirmation-modal.component';
import { SourceFolderModalComponent } from '../../components/modals/source-folder-modal/source-folder-modal.component';
import { TMaterialSourceFolder, TSourceMaterial } from '@core/api/materials/types';
import { ListItemComponent } from '@shared/components/content-panel';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { CommonService } from '@shared/services/common.service';
import { SourceMaterialsService } from '@shared/services/source-materials.service';

@Component({
  selector: 'app-materials-folders',
  templateUrl: './materials-folders.component.html',
  styleUrl: './materials-folders.component.scss',
  imports: [MatIcon, MatMenuModule, SpinnerComponent, ListItemComponent],
})
export class MaterialsFoldersComponent implements OnInit, OnDestroy {

  protected isLoading = false;

  public materials: TSourceMaterial[] | undefined;
  private _subs: Subscription[] = [];
  protected folders: TMaterialSourceFolder[] = [];
  protected selectedFolder: TMaterialSourceFolder | null = null;

  constructor(
    protected sourceMaterialService: SourceMaterialsService,
    protected dialog: MatDialog,
    private _commonServices: CommonService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this._subs.push(this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
        this.navigateToFolder();
      }),
    );
  }


  ngOnInit(): void {
    this.isLoading = true;
    this._subs.push(this.sourceMaterialService.folders$.subscribe(value => {
      this.folders = value.slice().sort((a, b) => a.name.localeCompare(b.name));
      this.isLoading = false;
      this.navigateToFolder();
    }));
    this._subs.push(this.sourceMaterialService.selectedFolder$.subscribe(value => {
      this.selectedFolder = value;
    }));
    this.sourceMaterialService.getFoldersList();
  }

  navigateToFolder() {
    const folderId = this.route.snapshot.firstChild?.paramMap.get('folder_id')
      ?? this.route.snapshot.firstChild?.paramMap.get('folderId')
      ?? this.router.parseUrl(this.router.url).root.children.primary?.segments
        .find(segment => segment.parameters.folder_id)?.parameters.folder_id
      ?? null;
    if (!folderId) {
      this.selectedFolder = null;
      return;
    }
    const foundFolder = this.folders.find(item => item._id === folderId) ?? null;
    this.sourceMaterialService.setFolder(foundFolder);
  }

  protected selectFolder(folder: TMaterialSourceFolder | null) {
    const params = folder ? { folder_id: folder._id } : {};
    this.router.navigate(['sources/materials', params]);
  }

  protected editFolder(item: TMaterialSourceFolder, _event?: Event) {
    const dialogRef = this.dialog.open(SourceFolderModalComponent, { data: item });
    const dialogSubscription = dialogRef.componentInstance.folderChanged.subscribe(updated => {
      if (this.selectedFolder?._id === updated._id) {
        this.sourceMaterialService.setFolder(updated);
      }
      dialogSubscription.unsubscribe();
    });
  }

  protected deleteFolder(item: TMaterialSourceFolder) {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: { text: `Are you sure you want to delete "${item.name}"?` },
    });
    const dialogSubscription = dialogRef.componentInstance.actionCallback.subscribe(() => {
      this.sourceMaterialService.deleteFolder(item._id!, () => {
        dialogRef.close();
        this.sourceMaterialService.setFolder(null);
        this.selectFolder(null);
        this._commonServices.openSuccessSnackBar(`Folder "${item.name}" was deleted`);
      });

      dialogSubscription.unsubscribe();
    });
  }

  ngOnDestroy() {
    this._subs.forEach(value => value.unsubscribe());
  }
}
